import React, { useEffect, useState } from "react";
import styles from "./Accordion.module.scss";
import useWindowSize from "../../hooks/useWindowSize";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Options_Modul_Akkordeon } from "@/wp/types/acf-module-options-types";
import {
  ExtendedLink,
  ExtendedLinkType,
} from "@/components/ExtededLink/ExtendedLink";
import { StaggerFadeInUp, FadeIn } from "@/components/Animation";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { ContentTag } from "@/components/ContentTag/ContentTag";
interface AccordionItemProps {
  elements: {
    title: string;
    content: string;
    link_extended: ExtendedLinkType;
  }[];
  border_line: {
    color: string;
    height: number;
  };
  separator: "box" | "border";
  box_color: string;
  moduleIndex: number;
  showExtendedLink: Options_Modul_Akkordeon["show_extended_link"];
  buttonType: Options_Modul_Akkordeon["button_type"];
}

export const AccordionItems = ({
  elements,
  border_line,
  separator,
  moduleIndex,
  showExtendedLink = false,
  buttonType,
}: AccordionItemProps) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const { width } = useWindowSize();
  const { getIcon } = useGlobalContext();

  const randomId = moduleIndex;

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const { key } = event;
    switch (key) {
      case "Enter":
      case " ":
        event.preventDefault();
        setOpenedIndex(openedIndex === index ? null : index);
        break;
      case "ArrowDown":
        event.preventDefault();
        focusNextHeader(index);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusPreviousHeader(index);
        break;
      case "Home":
        event.preventDefault();
        focusHeader(0);
        break;
      case "End":
        event.preventDefault();
        focusHeader(elements.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        setOpenedIndex(null);
        blurHeader();
        break;
      default:
        break;
    }
  };

  const focusNextHeader = (index: number) => {
    const nextIndex = index + 1 >= elements.length ? 0 : index + 1;
    focusHeader(nextIndex);
  };

  const focusPreviousHeader = (index: number) => {
    const prevIndex = index - 1 < 0 ? elements.length - 1 : index - 1;
    focusHeader(prevIndex);
  };

  const focusHeader = (index: number) => {
    const header = document.getElementById(`summary-${index}`);
    header?.focus();
  };

  const blurHeader = () => {
    const focusedElement = document.activeElement as HTMLElement;
    focusedElement.blur();
  };

  useEffect(() => {
    const details = document.querySelectorAll(
      `.details-${randomId}`,
    ) as NodeListOf<HTMLElement>;

    details.forEach((detail, index) => {
      const summary = detail.querySelector(
        `.summary-${randomId}`,
      ) as HTMLElement;
      const content = detail.querySelector(
        `.content-${randomId}`,
      ) as HTMLElement;

      if (summary) {
        // Calculate the height of the summary
        // the 8px is added so that we can add 2px margin to all sides of the summary
        const summaryHeight = summary.offsetHeight + 8;
        const contentHeight = content.offsetHeight;

        // Add the styles to the details tag
        if (openedIndex === index) {
          detail.style.maxHeight = `${summaryHeight + contentHeight}px`;
        } else {
          detail.style.maxHeight = `${summaryHeight}px`;
        }

        detail.style.overflow = "hidden";
        detail.style.transition = "all 0.3s ease-in-out";
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedIndex, width, elements]);

  return (
    <StaggerFadeInUp
      className={`${styles["accordion-items"]} ${styles[separator]}`}
    >
      {elements.map((element, index) => {
        return (
          // accordion item
          <FadeIn key={index}>
            <details
              className={`${styles["accordion-item"]} details-${randomId}`}
              style={{
                borderBottom:
                  separator === "border" && index < elements.length - 1
                    ? `${border_line.height}px solid var(--accordion-border-line-color)`
                    : "none",
              }}
              open
            >
              <summary
                className={`${styles["summary"]} summary-${randomId}  ${styles[separator]}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpenedIndex(openedIndex === index ? null : index);
                }}
                style={{
                  background:
                    separator === "box"
                      ? "var(--accordion-box-color)"
                      : "transparent",
                }}
                onKeyDown={(event) => handleKeyDown(event, index)}
                id={`summary-${index}`}
                aria-expanded={openedIndex === index}
                aria-controls={`accordion-content-${index}`}
              >
                <div
                  className={`${styles["accordion-item-header"]}`}
                  tabIndex={-1}
                >
                  <span className={`h3 has-text-left mr-4 ${styles.title}`}>
                    {element.title}
                  </span>
                  <span
                    className={`${styles["icon"]} ${openedIndex === index ? styles["is-open"] : ""}`}
                  >
                    <CustomSVG
                      style={{ fill: "var(--accordion-icons-color)" }}
                      width={36}
                      height={36}
                      src={getIcon("open")}
                      wrapper={"span"}
                      aria-hidden="true"
                      className="is-flex"
                    />
                  </span>
                </div>
              </summary>
              <div
                className={`${styles["accordion-item-content"]} content-${randomId}  ${styles[separator]}`}
                id={`accordion-content-${index}`}
                aria-hidden={openedIndex !== index}
              >
                <ContentTag disablePaddingBottom content={element.content} />

                {showExtendedLink && buttonType && (
                  <ExtendedLink
                    extendedLink={element.link_extended}
                    btnType={buttonType}
                  />
                )}
              </div>
            </details>
          </FadeIn>
        );
      })}
    </StaggerFadeInUp>
  );
};
